
// Import the public entity config AND the specific config item type
import { entityConfig as publicEntityConfigObject } from "@/lib/entityConfig"; // <-- Import public config object and rename
import { EntityConfigItem } from "@/lib/types";
import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the shape of the entity map
interface EntitiesMap {
  [entityKey: string]: EntityConfigItem<any>;
}

// The overall config holds the combined entities map
interface CombinedConfig {
  entities: EntitiesMap;
}

interface AppConfigContextType {
  config: CombinedConfig;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(
  undefined
);

interface AppConfigProviderProperties {
  children: ReactNode;
}

// Extract the entities map from the imported public config object
const publicEntitiesMap = publicEntityConfigObject as EntitiesMap;

const staticConfig: CombinedConfig = {
  entities: publicEntitiesMap,
};

// Define the component function separately
function AppConfigProviderComponent({
  children,
}: AppConfigProviderProperties): JSX.Element {
  // The config is now static, so we just provide it.
  // No more loading, error, or industry state needed.
  const [config] = useState<CombinedConfig>(staticConfig);

  const value = React.useMemo(
    () => ({
      config,
    }),
    [config]
  );

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error("useAppConfig must be used within an AppConfigProvider");
  }
  return context;
};

// Export the component
export const AppConfigProvider = AppConfigProviderComponent;
